import '../scripts/domainCheck';
import '../styles/globals.css';
import '../styles/tailwind.css';
import '../styles/manifold.css';
import 'react-phone-number-input/style.css';
import '@splidejs/react-splide/css/core';

import { ApolloProvider } from '@apollo/client';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { appWithTranslation, UserConfig } from 'next-i18next';

import nextI18NextConfig from '../../next-i18next.config';
import { csrClient } from '../apollo/api/avantArteClient';
import { AAGrowthbookProvider } from '../components/AAGrowthbookProvider';
import { DialogContainer } from '../components/Containers/Dialog';
import { DialogProvider } from '../components/Containers/DialogProvider';
import FooterLocaleVisibilityProvider from '../components/Containers/FooterLocaleVisibilityProvider';
import NavigationStateProvider from '../components/Containers/NavigationStateProvider';
import TimeProvider from '../components/Containers/TimeProvider';
import UserPreferencesProvider from '../components/Containers/UserPreferencesProvider';
import { GoogleOAuth } from '../components/GoogleOAuth';
import { NavigationContextProvider } from '../components/Layout/Navigation/NavigationContext';
import Web3CacheContextProvider from '../components/Web3/Web3Provider/Web3CacheProvider';
import { useUTMTags } from '../hooks/useUTMTags';
import { AuthProvider, FlowProvider, ViewportProvider } from '../providers';
import { AccountProvider } from '../providers/AccountProvider';
import { GoogleTagManager } from '../scripts/GoogleTagManager';
import { HubSpotTag } from '../scripts/HubSpot/HubSpotTag';
import { createAnalyticsClient } from '../utils/analytics/client';
import { google, mixpanel, sentry } from '../utils/analytics/providers';
import { AnalyticsRootProvider } from '../utils/analytics/root';

const analyticsClient = createAnalyticsClient([google, mixpanel, sentry]);

const DialogFlowContainer = dynamic(
  () => import('../components/Containers/Dialog/DialogFlowContainer'),
  { ssr: false },
);

const App = ({ Component, pageProps }: AppProps) => {
  useUTMTags();

  return (
    <NavigationContextProvider>
      <AnalyticsRootProvider context={{ client: analyticsClient }}>
        <ApolloProvider client={csrClient}>
          <FooterLocaleVisibilityProvider>
            <HubSpotTag />
            <UserPreferencesProvider>
              <FlowProvider>
                <DialogProvider>
                  <TimeProvider>
                    <AuthProvider>
                      <GoogleTagManager />
                      <AAGrowthbookProvider>
                        <Web3CacheContextProvider>
                          <ViewportProvider>
                            <NavigationStateProvider>
                              <GoogleOAuth />
                              <AccountProvider>
                                <Component {...pageProps} />
                                <DialogContainer />
                                <DialogFlowContainer />
                              </AccountProvider>
                            </NavigationStateProvider>
                          </ViewportProvider>
                        </Web3CacheContextProvider>
                      </AAGrowthbookProvider>
                    </AuthProvider>
                  </TimeProvider>
                </DialogProvider>
              </FlowProvider>
            </UserPreferencesProvider>
          </FooterLocaleVisibilityProvider>
        </ApolloProvider>
      </AnalyticsRootProvider>
    </NavigationContextProvider>
  );
};

// `next-i18next.config.js` is CommonJS and the installed `next-i18next` types
// are stricter than this project config shape; casting keeps runtime behavior correct.
export default appWithTranslation(
  App,
  nextI18NextConfig as unknown as UserConfig,
);
